"use client";

import {
	ArrowUpRight,
	Bot,
	Check,
	Copy,
	Image,
	MessageSquare,
	Server,
	Sparkles,
	Terminal,
	Zap,
} from "lucide-react";
import Link from "next/link";
import { useState, useCallback } from "react";

import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card } from "@/lib/components/card";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/lib/components/tabs";

import { useI18n } from "@llmgateway/i18n/react";

import type { Locale } from "@llmgateway/i18n";

interface Tool {
	name: string;
	description: string;
	icon: typeof MessageSquare;
	parameters: string[];
	example: string;
}

const tools: Tool[] = [
	{
		name: "chat",
		description:
			"Send messages to any LLM and get responses. Supports 200+ models from OpenAI, Anthropic, Google, and more.",
		icon: MessageSquare,
		parameters: ["model", "messages", "temperature", "max_tokens"],
		example: `{
  "model": "gpt-4o",
  "messages": [{ "role": "user", "content": "Hello!" }]
}`,
	},
	{
		name: "generate-image",
		description:
			"Generate images from text prompts using AI image models like Qwen Image. Returns images directly in the response.",
		icon: Image,
		parameters: ["prompt", "model", "size", "n"],
		example: `{
  "prompt": "A serene mountain landscape at sunset",
  "model": "qwen-image-plus",
  "size": "1024x1024"
}`,
	},
	{
		name: "generate-nano-banana",
		description:
			"Generate images with Gemini 3 Pro Image Preview. Returns inline image data. Set UPLOAD_DIR on the server to also save images to disk.",
		icon: Sparkles,
		parameters: ["prompt", "filename", "aspect_ratio"],
		example: `{
  "prompt": "A pixel-art cat sitting on a rainbow",
  "filename": "hero-image.png",
  "aspect_ratio": "16:9"
}`,
	},
	{
		name: "list-models",
		description:
			"Discover available models with their capabilities, pricing, and provider information.",
		icon: Server,
		parameters: ["family", "limit", "include_deactivated"],
		example: `{
  "family": "openai",
  "limit": 10
}`,
	},
	{
		name: "list-image-models",
		description:
			"Get a list of all available image generation models with pricing and usage examples.",
		icon: Sparkles,
		parameters: [],
		example: `// No parameters required
// Returns: qwen-image-plus, qwen-image-max, etc.`,
	},
];

const featureIcons = {
	server: Server,
	image: Image,
	zap: Zap,
	terminal: Terminal,
};

const localizedMcpCopy = {
	en: {
		tabs: {
			humans: "For Humans",
			agents: "For AI Agents",
		},
		featureCards: [
			{
				icon: featureIcons.server,
				title: "200+ Models",
				description: "Access models from OpenAI, Anthropic, Google & more",
			},
			{
				icon: featureIcons.image,
				title: "Image Generation",
				description: "Generate images directly from your AI assistant",
			},
			{
				icon: featureIcons.zap,
				title: "Unified API",
				description: "One endpoint for all providers and capabilities",
			},
			{
				icon: featureIcons.terminal,
				title: "Easy Setup",
				description: "Works with Claude Code, Cursor, and any MCP client",
			},
		],
		toolDescriptions: {
			chat: tools[0].description,
			"generate-image": tools[1].description,
			"generate-nano-banana": tools[2].description,
			"list-models": tools[3].description,
			"list-image-models": tools[4].description,
		},
		availableTools: "Available Tools",
		noParams: "No params",
		quickStart: "Quick Start",
		runClaudeCommand:
			"Run this command in your terminal to add the MCP server globally.",
		manualJson: "Alternative: Manual JSON configuration",
		addTo: "Add to",
		setEnvFirst: "Set {env} env var first, then run this command.",
		manualToml: "Alternative: Manual TOML configuration",
		directApiCall: "Direct API Call",
		ctaTitle: "Ready to get started?",
		ctaDescription:
			"Get your API key and start using doteb with your favorite AI assistant.",
		getApiKey: "Get API Key",
		readDocumentation: "Read Documentation",
		referenceTitle: "MCP Server Reference (Plain Markdown)",
		copied: "Copied",
		copyAll: "Copy All",
		agentsFootnote:
			"This plain markdown format is optimized for AI agents and LLMs to parse and understand.",
		agentMarkdown: "",
	},
	"zh-CN": {
		tabs: {
			humans: "面向用户",
			agents: "面向 AI Agent",
		},
		featureCards: [
			{
				icon: featureIcons.server,
				title: "200+ 模型",
				description: "接入 OpenAI、Anthropic、Google 等供应商模型",
			},
			{
				icon: featureIcons.image,
				title: "图像生成",
				description: "直接从你的 AI 助手中生成图像",
			},
			{
				icon: featureIcons.zap,
				title: "统一 API",
				description: "一个 endpoint 覆盖所有供应商和能力",
			},
			{
				icon: featureIcons.terminal,
				title: "快速配置",
				description: "支持 Claude Code、Cursor 和任意 MCP 客户端",
			},
		],
		toolDescriptions: {
			chat: "向任意 LLM 发送消息并获取响应。支持来自 OpenAI、Anthropic、Google 等供应商的 200+ 模型。",
			"generate-image":
				"使用 Qwen Image 等 AI 图像模型从文本 prompt 生成图像，并直接在响应中返回图片。",
			"generate-nano-banana":
				"使用 Gemini 3 Pro Image Preview 生成图像，返回内联图片数据。服务端配置 UPLOAD_DIR 后也可保存到磁盘。",
			"list-models": "发现可用模型及其能力、价格和供应商信息。",
			"list-image-models": "获取所有可用图像生成模型及其价格和使用示例。",
		},
		availableTools: "可用工具",
		noParams: "无参数",
		quickStart: "快速开始",
		runClaudeCommand: "在终端运行此命令，将 MCP server 添加为全局配置。",
		manualJson: "替代方式：手动 JSON 配置",
		addTo: "添加到",
		setEnvFirst: "先设置 {env} 环境变量，然后运行此命令。",
		manualToml: "替代方式：手动 TOML 配置",
		directApiCall: "直接 API 调用",
		ctaTitle: "准备好开始了吗？",
		ctaDescription:
			"获取 API key，并开始在你常用的 AI 助手中使用 doteb。",
		getApiKey: "获取 API Key",
		readDocumentation: "阅读文档",
		referenceTitle: "MCP Server 参考（纯 Markdown）",
		copied: "已复制",
		copyAll: "复制全部",
		agentsFootnote: "这种纯 Markdown 格式更适合 AI agent 和 LLM 解析理解。",
		agentMarkdown: `# doteb MCP Server

## Endpoint
\`https://api.doteb.com/mcp\`

## 认证
- Header: \`Authorization: Bearer YOUR_API_KEY\`
- 或：\`x-api-key: YOUR_API_KEY\`

## 协议
- JSON-RPC 2.0
- MCP Version: 2024-11-05
- Transport: HTTP Streamable

## 可用工具

### chat
向任意 LLM 模型发送消息。

**参数：**
- \`model\` (string, required): 模型 ID，例如 "gpt-4o" 或 "claude-sonnet-4-20250514"
- \`messages\` (array, required): {role, content} 对象数组
- \`temperature\` (number, optional): 0-2
- \`max_tokens\` (number, optional): 最大 token 数

### generate-image
根据文本 prompt 生成图像。

**参数：**
- \`prompt\` (string, required): 图像描述
- \`model\` (string, optional): 默认 "qwen-image-plus"
- \`size\` (string, optional): 默认 "1024x1024"
- \`n\` (number, optional): 1-4 张图像，默认 1

### generate-nano-banana
使用 Gemini 3 Pro Image Preview ("Nano Banana") 生成图像。默认返回内联图片数据；只有服务端配置 UPLOAD_DIR 时才会保存到磁盘。

**参数：**
- \`prompt\` (string, required): 图像描述
- \`filename\` (string, optional): 文件名，不允许路径分隔符
- \`aspect_ratio\` (string, optional): "1:1"、"16:9"、"4:3" 或 "5:4"

### list-models
列出可用 LLM 模型。

### list-image-models
列出可用图像生成模型，无需参数。

## MCP 配置

### Claude Code
\`\`\`bash
claude mcp add --transport http --scope user llmgateway https://api.doteb.com/mcp \\
  --header "Authorization: Bearer YOUR_API_KEY"
\`\`\`

### Codex
\`\`\`bash
codex mcp add llmgateway --url https://api.doteb.com/mcp \\
  --bearer-token-env-var LLM_GATEWAY_API_KEY
\`\`\`

### Cursor (~/.cursor/mcp.json)
\`\`\`json
{
  "mcpServers": {
    "llmgateway": {
      "url": "https://api.doteb.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}
\`\`\`

## JSON-RPC 方法
- \`initialize\`: 握手
- \`tools/list\`: 列出可用工具
- \`tools/call\`: 执行工具
- \`ping\`: 健康检查

## 链接
- Documentation: https://docs.doteb.com/guides/mcp
- Models: https://doteb.com/models
- Dashboard: https://doteb.com/dashboard
- API Keys: https://doteb.com/dashboard/keys`,
	},
} satisfies Record<
	Locale,
	{
		tabs: { humans: string; agents: string };
		featureCards: Array<{
			icon: typeof MessageSquare;
			title: string;
			description: string;
		}>;
		toolDescriptions: Record<string, string>;
		availableTools: string;
		noParams: string;
		quickStart: string;
		runClaudeCommand: string;
		manualJson: string;
		addTo: string;
		setEnvFirst: string;
		manualToml: string;
		directApiCall: string;
		ctaTitle: string;
		ctaDescription: string;
		getApiKey: string;
		readDocumentation: string;
		referenceTitle: string;
		copied: string;
		copyAll: string;
		agentsFootnote: string;
		agentMarkdown: string;
	}
>;

function getMcpCopy(locale: Locale) {
	return locale === "zh-CN"
		? localizedMcpCopy["zh-CN"]
		: { ...localizedMcpCopy.en, agentMarkdown: aiAgentMarkdown };
}

const configExamples = {
	claudeCodeCli: `claude mcp add --transport http --scope user llmgateway https://api.doteb.com/mcp \\
  --header "Authorization: Bearer YOUR_API_KEY"`,
	claudeCode: `{
  "mcpServers": {
    "llmgateway": {
      "url": "https://api.doteb.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
	codexCli: `codex mcp add llmgateway --url https://api.doteb.com/mcp \\
  --bearer-token-env-var LLM_GATEWAY_API_KEY`,
	codex: `[mcp_servers.llmgateway]
url = "https://api.doteb.com/mcp"
bearer_token_env_var = "LLM_GATEWAY_API_KEY"`,
	cursor: `{
  "mcpServers": {
    "llmgateway": {
      "url": "https://api.doteb.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
	curl: `curl -X POST https://api.doteb.com/mcp \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "jsonrpc": "2.0",
    "id": 1,
    "method": "tools/call",
    "params": {
      "name": "chat",
      "arguments": {
        "model": "gpt-4o",
        "messages": [{"role": "user", "content": "Hello!"}]
      }
    }
  }'`,
};

// Plain markdown content for AI agents
const aiAgentMarkdown = `# doteb MCP Server

## Endpoint
\`https://api.doteb.com/mcp\`

## Authentication
- Header: \`Authorization: Bearer YOUR_API_KEY\`
- Or: \`x-api-key: YOUR_API_KEY\`

## Protocol
- JSON-RPC 2.0
- MCP Version: 2024-11-05
- Transport: HTTP Streamable

## Available Tools

### chat
Send messages to any LLM model.

**Parameters:**
- \`model\` (string, required): Model ID (e.g., "gpt-4o", "claude-sonnet-4-20250514")
- \`messages\` (array, required): Array of {role, content} objects
- \`temperature\` (number, optional): 0-2
- \`max_tokens\` (number, optional): Maximum tokens

**Example:**
\`\`\`json
{
  "name": "chat",
  "arguments": {
    "model": "gpt-4o",
    "messages": [{"role": "user", "content": "Hello"}]
  }
}
\`\`\`

### generate-image
Generate images from text prompts.

**Parameters:**
- \`prompt\` (string, required): Image description
- \`model\` (string, optional): Default "qwen-image-plus"
- \`size\` (string, optional): Default "1024x1024"
- \`n\` (number, optional): 1-4 images, default 1

**Example:**
\`\`\`json
{
  "name": "generate-image",
  "arguments": {
    "prompt": "A sunset over mountains",
    "model": "qwen-image-max"
  }
}
\`\`\`

### generate-nano-banana
Generate images with Gemini 3 Pro Image Preview ("Nano Banana"). Returns inline image data. Images are only saved to disk when the server has UPLOAD_DIR configured.

**Parameters:**
- \`prompt\` (string, required): Image description
- \`filename\` (string, optional): Filename (no path separators). Default: nano-banana-{timestamp}.png
- \`aspect_ratio\` (string, optional): "1:1", "16:9", "4:3", or "5:4"

**Example:**
\`\`\`json
{
  "name": "generate-nano-banana",
  "arguments": {
    "prompt": "A pixel-art cat on a rainbow",
    "aspect_ratio": "16:9"
  }
}
\`\`\`

### list-models
List available LLM models.

**Parameters:**
- \`family\` (string, optional): Filter by provider (openai, anthropic, google)
- \`limit\` (number, optional): Max results, default 20
- \`include_deactivated\` (boolean, optional): Include inactive models

### list-image-models
List available image generation models. No parameters required.

## Supported Models
- OpenAI: gpt-4o, gpt-4o-mini, o1, o3-mini
- Anthropic: claude-sonnet-4-20250514, claude-opus-4-5
- Google: gemini-2.0-flash, gemini-pro
- Image: qwen-image-plus, qwen-image-max
- And 200+ more at https://doteb.com/models

## MCP Configuration

### Claude Code
\`\`\`bash
claude mcp add --transport http --scope user llmgateway https://api.doteb.com/mcp \\
  --header "Authorization: Bearer YOUR_API_KEY"
\`\`\`

### Codex
\`\`\`bash
codex mcp add llmgateway --url https://api.doteb.com/mcp \\
  --bearer-token-env-var LLM_GATEWAY_API_KEY
\`\`\`

### Cursor (~/.cursor/mcp.json)
\`\`\`json
{
  "mcpServers": {
    "llmgateway": {
      "url": "https://api.doteb.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}
\`\`\`

## JSON-RPC Methods
- \`initialize\`: Handshake
- \`tools/list\`: List available tools
- \`tools/call\`: Execute a tool
- \`ping\`: Health check

## Links
- Documentation: https://docs.doteb.com/guides/mcp
- Models: https://doteb.com/models
- Dashboard: https://doteb.com/dashboard
- API Keys: https://doteb.com/dashboard/keys`;

export function McpContent() {
	const { locale } = useI18n();
	const copy = getMcpCopy(locale);
	const [copiedConfig, setCopiedConfig] = useState<string | null>(null);
	const toolDescriptions: Record<string, string> = copy.toolDescriptions;
	const localizedTools = tools.map((tool) => ({
		...tool,
		description: toolDescriptions[tool.name] ?? tool.description,
	}));

	const copyToClipboard = useCallback((text: string, key: string) => {
		void navigator.clipboard.writeText(text);
		setCopiedConfig(key);
		setTimeout(() => setCopiedConfig(null), 2000);
	}, []);

	return (
		<Tabs defaultValue="humans" className="w-full">
			<div className="flex justify-center mb-8">
				<TabsList className="grid w-full max-w-md grid-cols-2">
					<TabsTrigger value="humans" className="gap-2">
						<Zap className="h-4 w-4" />
						{copy.tabs.humans}
					</TabsTrigger>
					<TabsTrigger value="agents" className="gap-2">
						<Bot className="h-4 w-4" />
						{copy.tabs.agents}
					</TabsTrigger>
				</TabsList>
			</div>

			<TabsContent value="humans" className="space-y-16">
				{/* Hero Features */}
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
					{copy.featureCards.map((feature) => (
						<Card
							key={feature.title}
							className="p-6 bg-gradient-to-br from-background to-muted/30 border-0 shadow-lg"
						>
							<feature.icon className="h-8 w-8 text-primary mb-3" />
							<h3 className="font-semibold mb-1">{feature.title}</h3>
							<p className="text-sm text-muted-foreground">
								{feature.description}
							</p>
						</Card>
					))}
				</div>

				{/* Available Tools */}
				<div>
					<h2 className="text-2xl font-bold text-center mb-8">
						{copy.availableTools}
					</h2>
					<div className="grid gap-6 sm:grid-cols-2 max-w-4xl mx-auto">
						{localizedTools.map((tool) => (
							<Card
								key={tool.name}
								className="group relative overflow-hidden border-0 bg-gradient-to-br from-background to-muted/30 shadow-xl hover:shadow-2xl transition-all duration-500"
							>
								<div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
								<div className="relative p-6 space-y-4">
									<div className="flex items-center gap-3">
										<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 shadow-lg">
											<tool.icon className="h-6 w-6 text-primary-foreground" />
										</div>
										<div>
											<h3 className="text-lg font-bold font-mono">
												{tool.name}
											</h3>
											<div className="flex gap-1 flex-wrap mt-1">
												{tool.parameters.length === 0 ? (
													<Badge variant="outline" className="text-xs">
														{copy.noParams}
													</Badge>
												) : (
													<>
														{tool.parameters.slice(0, 3).map((param) => (
															<Badge
																key={param}
																variant="secondary"
																className="text-xs"
															>
																{param}
															</Badge>
														))}
													</>
												)}
												{tool.parameters.length > 3 && (
													<Badge variant="secondary" className="text-xs">
														+{tool.parameters.length - 3}
													</Badge>
												)}
											</div>
										</div>
									</div>
									<p className="text-sm text-muted-foreground">
										{tool.description}
									</p>
									<div className="relative">
										<pre className="bg-muted/50 rounded-lg p-3 text-xs overflow-x-auto">
											<code>{tool.example}</code>
										</pre>
									</div>
								</div>
								<div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/50 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
							</Card>
						))}
					</div>
				</div>

				{/* Quick Start */}
				<div>
					<h2 className="text-2xl font-bold text-center mb-8">
						{copy.quickStart}
					</h2>
					<div className="max-w-3xl mx-auto space-y-6">
						{/* Claude Code */}
						<Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-background to-muted/30">
							<div className="flex items-center justify-between mb-4">
								<div className="flex items-center gap-2">
									<Terminal className="h-5 w-5 text-primary" />
									<span className="font-semibold">Claude Code</span>
								</div>
								<Button
									variant="ghost"
									size="sm"
									onClick={() =>
										copyToClipboard(configExamples.claudeCodeCli, "claude-cli")
									}
								>
									{copiedConfig === "claude-cli" ? (
										<Check className="h-4 w-4" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</Button>
							</div>
							<pre className="bg-muted/50 rounded-lg p-4 text-sm overflow-x-auto">
								<code>{configExamples.claudeCodeCli}</code>
							</pre>
							<p className="text-sm text-muted-foreground mt-3">
								{copy.runClaudeCommand}
							</p>
							<details className="mt-4">
								<summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
									{copy.manualJson}
								</summary>
								<div className="mt-3">
									<div className="flex items-center justify-between mb-2">
										<span className="text-xs text-muted-foreground">
											{copy.addTo}{" "}
											<code className="bg-muted px-1 rounded">
												~/.claude/claude_desktop_config.json
											</code>
										</span>
										<Button
											variant="ghost"
											size="sm"
											onClick={() =>
												copyToClipboard(configExamples.claudeCode, "claude")
											}
										>
											{copiedConfig === "claude" ? (
												<Check className="h-3 w-3" />
											) : (
												<Copy className="h-3 w-3" />
											)}
										</Button>
									</div>
									<pre className="bg-muted/50 rounded-lg p-4 text-sm overflow-x-auto">
										<code>{configExamples.claudeCode}</code>
									</pre>
								</div>
							</details>
						</Card>

						{/* Codex */}
						<Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-background to-muted/30">
							<div className="flex items-center justify-between mb-4">
								<div className="flex items-center gap-2">
									<Terminal className="h-5 w-5 text-primary" />
									<span className="font-semibold">Codex</span>
								</div>
								<Button
									variant="ghost"
									size="sm"
									onClick={() =>
										copyToClipboard(configExamples.codexCli, "codex-cli")
									}
								>
									{copiedConfig === "codex-cli" ? (
										<Check className="h-4 w-4" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</Button>
							</div>
							<pre className="bg-muted/50 rounded-lg p-4 text-sm overflow-x-auto">
								<code>{configExamples.codexCli}</code>
							</pre>
							<p className="text-sm text-muted-foreground mt-3">
								{locale === "zh-CN" ? "先设置 " : "Set "}
								<code className="bg-muted px-1 rounded">
									LLM_GATEWAY_API_KEY
								</code>
								{locale === "zh-CN"
									? " 环境变量，然后运行此命令。"
									: " env var first, then run this command."}
							</p>
							<details className="mt-4">
								<summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
									{copy.manualToml}
								</summary>
								<div className="mt-3">
									<div className="flex items-center justify-between mb-2">
										<span className="text-xs text-muted-foreground">
											{copy.addTo}{" "}
											<code className="bg-muted px-1 rounded">
												~/.codex/config.toml
											</code>
										</span>
										<Button
											variant="ghost"
											size="sm"
											onClick={() =>
												copyToClipboard(configExamples.codex, "codex")
											}
										>
											{copiedConfig === "codex" ? (
												<Check className="h-3 w-3" />
											) : (
												<Copy className="h-3 w-3" />
											)}
										</Button>
									</div>
									<pre className="bg-muted/50 rounded-lg p-4 text-sm overflow-x-auto">
										<code>{configExamples.codex}</code>
									</pre>
								</div>
							</details>
						</Card>

						{/* Cursor */}
						<Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-background to-muted/30">
							<div className="flex items-center justify-between mb-4">
								<div className="flex items-center gap-2">
									<Terminal className="h-5 w-5 text-primary" />
									<span className="font-semibold">Cursor</span>
								</div>
								<Button
									variant="ghost"
									size="sm"
									onClick={() =>
										copyToClipboard(configExamples.cursor, "cursor")
									}
								>
									{copiedConfig === "cursor" ? (
										<Check className="h-4 w-4" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</Button>
							</div>
							<pre className="bg-muted/50 rounded-lg p-4 text-sm overflow-x-auto">
								<code>{configExamples.cursor}</code>
							</pre>
							<p className="text-sm text-muted-foreground mt-3">
								{copy.addTo}{" "}
								<code className="bg-muted px-1 rounded">
									~/.cursor/mcp.json
								</code>
							</p>
						</Card>

						{/* Direct API Call */}
						<Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-background to-muted/30">
							<div className="flex items-center justify-between mb-4">
								<div className="flex items-center gap-2">
									<Terminal className="h-5 w-5 text-primary" />
									<span className="font-semibold">{copy.directApiCall}</span>
								</div>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => copyToClipboard(configExamples.curl, "curl")}
								>
									{copiedConfig === "curl" ? (
										<Check className="h-4 w-4" />
									) : (
										<Copy className="h-4 w-4" />
									)}
								</Button>
							</div>
							<pre className="bg-muted/50 rounded-lg p-4 text-sm overflow-x-auto">
								<code>{configExamples.curl}</code>
							</pre>
						</Card>
					</div>
				</div>

				{/* CTA */}
				<div className="text-center space-y-6">
					<h2 className="text-2xl font-bold">{copy.ctaTitle}</h2>
					<p className="text-muted-foreground max-w-lg mx-auto">
						{copy.ctaDescription}
					</p>
					<div className="flex gap-4 justify-center flex-wrap">
						<Button asChild size="lg">
							<Link href="/dashboard/keys">
								{copy.getApiKey}
								<ArrowUpRight className="ml-2 h-4 w-4" />
							</Link>
						</Button>
						<Button asChild variant="outline" size="lg">
							<Link href="https://docs.doteb.com/guides/mcp">
								{copy.readDocumentation}
								<ArrowUpRight className="ml-2 h-4 w-4" />
							</Link>
						</Button>
					</div>
				</div>
			</TabsContent>

			<TabsContent value="agents" className="space-y-8">
				<Card className="p-6 border-0 shadow-xl bg-gradient-to-br from-background to-muted/30">
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center gap-2">
							<Bot className="h-5 w-5 text-primary" />
							<span className="font-semibold">{copy.referenceTitle}</span>
						</div>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => copyToClipboard(copy.agentMarkdown, "markdown")}
						>
							{copiedConfig === "markdown" ? (
								<>
									<Check className="h-4 w-4 mr-2" />
									{copy.copied}
								</>
							) : (
								<>
									<Copy className="h-4 w-4 mr-2" />
									{copy.copyAll}
								</>
							)}
						</Button>
					</div>
					<div className="bg-muted/50 rounded-lg p-6 font-mono text-sm whitespace-pre-wrap overflow-x-auto max-h-[70vh] overflow-y-auto">
						{copy.agentMarkdown}
					</div>
				</Card>

				<p className="text-center text-muted-foreground text-sm">
					{copy.agentsFootnote}
				</p>
			</TabsContent>
		</Tabs>
	);
}
